import React, { useState, useEffect } from 'react';
import { Alert, Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useRoleAccess } from '../../../hooks/useRoleAccess';
import { isPastDueInGrace, formatGraceDeadline } from '../../../utils/pastDue';
import { trackUnsubscribe } from '../../../utils/analytics';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import styles from './SubscriptionDetails.module.css';

import PlanHeroCard from './components/planHero/PlanHeroCard';
import PlanHeroSkeleton from './components/planHero/PlanHeroSkeleton';
import { getPlanHeroState } from './components/planHero/planHeroState';
import PlanUsageCard from './components/planUsage/PlanUsageCard';
import DetailsCard from './components/DetailsCard';
import AddonSubscriptionCard from './components/AddonSubscriptionCard';
import PaymentsTable from './components/payments/PaymentsTable';
import SubscriptionTimeline from './components/timeline/SubscriptionTimeline';
import CancelSubscriptionModal from './components/CancelSubscriptionModal';
import addonStyles from './components/addons/Addons.module.css';

import useSubscriptionData from './hooks/useSubscriptionData';
import usePlanUsage from './hooks/usePlanUsage';
import { formatDayMonthYear } from './utils/subscriptionFormat';

const CANCEL_BLOCKED_TEXT = 'Só a pessoa responsável pela conta ou um administrador pode cancelar a assinatura.';

// Tela "Meu plano": orquestra o card principal, o uso, os adicionais e os históricos
const SubscriptionDetails = () => {
  const location = useLocation();

  // Contexto de assinatura (trial interno)
  const { isInActiveTrial, getTrialDaysRemaining, trialInfo, hasActiveSubscription, refreshSubscription, invalidateCache, lastActivePlan } = useSubscription();
  const inTrial = isInActiveTrial();
  const daysRemaining = getTrialDaysRemaining();

  // Papel: quem gerencia (portal de pagamento) e quem cancela
  const { canAccess } = useRoleAccess();
  const canManageSubscription = canAccess('subscription');
  const canCancelSubscription = canAccess('subscriptionCancel');

  const usage = usePlanUsage();

  // Se chegou via navigate pós-upgrade, forçar refresh do contexto para garantir
  // que planConfig e subscription reflitam o novo plano (simula F5 sem recarregar)
  useEffect(() => {
    if (location.state?.upgraded) {
      invalidateCache();
      refreshSubscription(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    subscription,
    addons,
    payments,
    history,
    plans,
    nextCharge,
    paymentsPagination,
    historyPagination,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    setPaymentsPage,
    setHistoryPage,
    cancelSubscription,
    cancelSpecificSubscription,
    manageStripeSubscription,
  } = useSubscriptionData();

  // Popup de cancelamento
  const [cancelPopupOpen, setCancelPopupOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(null);

  const handleCancelPopupClose = () => {
    setCancelPopupOpen(false);
    setCancelSuccess(null);
  };

  const handleCancelSubscription = (reason, details) => {
    setCancelLoading(true);
    cancelSubscription((result) => {
      setCancelLoading(false);
      if (result.success) {
        trackUnsubscribe({
          plan: subscription?.plan_name || 'unknown',
          reason: reason || 'user_requested'
        });
        setCancelSuccess(true);
      } else {
        setCancelSuccess(false);
      }
    }, reason, details);
  };

  const handleManageStripeSubscription = () => {
    manageStripeSubscription();
  };

  // Já assinou alguma vez (trial_used vira 1 no cadastro, então não serve para isso)
  const hadSubscription = !!lastActivePlan || !!subscription?.stripe_subscription_id;
  const hasPlan = !!subscription?.has_active_plan;
  const statusLower = subscription?.subscription_status?.toLowerCase() || '';
  const pastDueInGrace = isPastDueInGrace(subscription);
  const graceDeadline = formatGraceDeadline(subscription?.past_due_grace_ends_at);
  const trialExpired = !!trialInfo && !trialInfo.is_trial_active && !hasActiveSubscription() && !hadSubscription;

  const heroState = getPlanHeroState({
    subscription,
    inTrial,
    trialDaysRemaining: daysRemaining,
    trialExpired,
    pastDueInGrace,
    graceDeadline,
  });

  const canCancelNow = subscription && (statusLower === 'active' || statusLower === 'trialing');

  return (
    <div className='page-content'>
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>Meu plano</h1>
          <p className={styles.subtitle}>Seu plano, o uso da sua conta e os pagamentos em um só lugar.</p>
        </header>

        {error && (
          <Alert severity="error" sx={{ borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        <div className={styles.grid}>
          <div className={styles.column}>
            {loading.subscription ? (
              <PlanHeroSkeleton />
            ) : (
              <PlanHeroCard
                subscription={subscription}
                heroState={heroState}
                nextCharge={nextCharge}
                trialInfo={trialInfo}
                trialDaysRemaining={daysRemaining}
                pastDueInGrace={pastDueInGrace}
                graceDeadline={graceDeadline}
                canManage={canManageSubscription}
                onManage={handleManageStripeSubscription}
              />
            )}

            {subscription && (
              <PaymentsTable
                payments={payments}
                pagination={paymentsPagination}
                onPageChange={setPaymentsPage}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                loading={loading.payments}
              />
            )}
          </div>

          <div className={styles.column}>
            <PlanUsageCard
              usage={usage}
              hasPlan={hasPlan || inTrial}
              plans={plans}
              currentPlanId={subscription?.subscription_plan_id}
            />

            {addons && addons.length > 0 && (
              <DetailsCard title="Adicionais">
                <div className={addonStyles.list}>
                  {addons.map(addon => (
                    <AddonSubscriptionCard
                      key={addon.id}
                      addon={addon}
                      onCancelAddon={cancelSpecificSubscription}
                    />
                  ))}
                </div>
              </DetailsCard>
            )}

            {subscription && (
              <SubscriptionTimeline
                history={history}
                pagination={historyPagination}
                onPageChange={setHistoryPage}
                plans={plans}
                loading={loading.history}
              />
            )}
          </div>
        </div>

        {/* Cancelamento: sempre visível com plano ativo; bloqueado (com o motivo) para quem não gerencia */}
        {canCancelNow && (
          <footer className={styles.footer}>
            <Tooltip title={canCancelSubscription ? '' : CANCEL_BLOCKED_TEXT} arrow>
              <span>
                <button
                  type="button"
                  className={styles.cancelLink}
                  onClick={() => setCancelPopupOpen(true)}
                  disabled={!canCancelSubscription}
                >
                  Cancelar assinatura
                </button>
              </span>
            </Tooltip>
            {!canCancelSubscription && <span className={styles.footerNote}>{CANCEL_BLOCKED_TEXT}</span>}
          </footer>
        )}
      </div>

      {/* next_due_date é 'YYYY-MM-DD' (dia de Brasília): formatado sem new Date(), que leria como UTC */}
      <CancelSubscriptionModal
        isOpen={cancelPopupOpen}
        onClose={handleCancelPopupClose}
        onConfirm={handleCancelSubscription}
        isLoading={cancelLoading}
        success={cancelSuccess}
        subscriptionEndDate={formatDayMonthYear(subscription?.next_due_date)}
      />
    </div>
  );
};

export default SubscriptionDetails;
